import React, { useState } from "react";
import {
  Users,
  UserCheck,
  Trophy,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Mail,
  Award,
  Edit,
  Trash2,
  Key,
  CheckCircle,
  UserPlus
} from "lucide-react";
import CreateJobSeekerModal from "../../components/AdminPanel/CreateJobSeekerModal";
import EditUserModal from "../../components/AdminPanel/EditUserModal";
import DeleteUserModal from "../../components/AdminPanel/DeleteUserModal";
import PasswordResetModal from "../../components/AdminPanel/PasswordResetModal";
import { adminService } from "../../services/adminService";
import { toast } from "react-hot-toast";

const JobseekersTab = ({
  jobSeekers,
  tests,
  testResults,
  expandedSections,
  toggleSection,
  formatDate,
  getResultsForJobSeeker,
  onRefresh
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const toggleRow = (seekerId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(seekerId)) {
        newSet.delete(seekerId);
      } else {
        newSet.add(seekerId);
      }
      return newSet;
    });
  };

  const handleEdit = (seeker, e) => {
    e.stopPropagation();
    setSelectedUser(seeker);
    setEditModalOpen(true);
  };

  const handleDelete = (seeker, e) => {
    e.stopPropagation();
    setSelectedUser(seeker);
    setDeleteModalOpen(true);
  };

  const handlePasswordReset = (seeker, e) => {
    e.stopPropagation();
    setSelectedUser(seeker);
    setPasswordResetModalOpen(true);
  };

  const handleVerify = async (seeker, e) => {
    e.stopPropagation();

    const result = await adminService.manuallyVerifyJobSeeker(seeker.id);

    if (result.success) {
      toast.success(`${seeker.name} h manually verified!`);
      onRefresh();
    } else {
      toast.error(result.error || 'Failed to verify job seeker');
    }
  };

  return (
    <div className="space-y-6">
      {/* Create Job Seeker Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          <span>Create Job Seeker</span>
        </button>
      </div>

      {/* Job Seekers */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div
          className="p-6 border-b border-gray-200 cursor-pointer flex items-center justify-between"
          onClick={() => toggleSection("candidates")}
        >
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Job Seekers & Information ({jobSeekers.length})
          </h2>
          {expandedSections.candidates ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.candidates && (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-12 px-3 py-3"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobSeekers.map((seeker) => {
                    const isExpanded = expandedRows.has(seeker.id);
                    return (
                      <React.Fragment key={seeker.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleRow(seeker.id)}
                              className="text-gray-400 hover:text-gray-600 transition-transform duration-200"
                              style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                            >
                              <ChevronRight className="w-5 h-5" />
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                <UserCheck className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 flex items-center space-x-2">
                                  <span>{seeker.name}</span>
                                  {seeker.skillsVerified && (
                                    <CheckCircle className="w-4 h-4 text-green-600" title="Skills Verified" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {!seeker.skillsVerified && (
                                <button
                                  onClick={(e) => handleVerify(seeker, e)}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Manually Verify Skills"
                                >
                                  <CheckCircle className="w-5 h-5" />
                                </button>
                              )}
                              <button
                                onClick={(e) => handleEdit(seeker, e)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit User"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => handlePasswordReset(seeker, e)}
                                className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                                title="Send Password Reset"
                              >
                                <Key className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => handleDelete(seeker, e)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete User"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={3} className="px-6 py-4 bg-gray-50">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-12">
                                {/* Email */}
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0">
                                    <Mail className="w-5 h-5 text-blue-500" />
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase">Email</p>
                                    <p className="mt-1 text-sm text-gray-900">{seeker.email}</p>
                                  </div>
                                </div>

                                {/* Joined Date */}
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0">
                                    <Calendar className="w-5 h-5 text-green-500" />
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase">Joined Date</p>
                                    <p className="mt-1 text-sm text-gray-900">{formatDate(seeker.createdAt)}</p>
                                  </div>
                                </div>

                                {/* Tests Taken */}
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0">
                                    <Award className="w-5 h-5 text-purple-500" />
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase">Tests Taken</p>
                                    <p className="mt-1">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        {getResultsForJobSeeker(seeker.id).length} tests
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Test Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div
          className="p-6 border-b border-gray-200 cursor-pointer flex items-center justify-between"
          onClick={() => toggleSection("candidateResults")}
        >
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Trophy className="w-5 h-5 mr-2" />
            Test Results & Scores ({testResults.length})
          </h2>
          {expandedSections.candidateResults ? (
            <ChevronUp />
          ) : (
            <ChevronDown />
          )}
        </div>
        {expandedSections.candidateResults && (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Candidate
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Test
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {testResults.map((result) => {
                    const candidate = jobSeekers.find(
                      (js) => js.id === result.userId
                    );
                    const test = tests.find(
                      (t) => t.id === result.testId
                    );
                    const scoreValue = result.score !== undefined && result.score !== null ? result.score : 0;
                    const hasScore = result.score !== undefined && result.score !== null;

                    return (
                      <tr key={result.id} className="hover:bg-gray-50">
                        {/* Candidate Name */}
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                              <UserCheck className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                              {candidate?.name || result.userName || "Unknown User"}
                            </div>
                          </div>
                        </td>

                        {/* Email */}
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {candidate?.email || "N/A"}
                        </td>

                        {/* Test Name */}
                        <td className="px-4 sm:px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {test?.title || result.testTitle || "Unknown Test"}
                          </div>
                        </td>

                        {/* Score */}
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          {hasScore ? (
                            <div className="flex flex-col gap-1">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  scoreValue >= 80
                                    ? "bg-green-100 text-green-800"
                                    : scoreValue >= 60
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {scoreValue}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500 italic">
                              No score available
                            </span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          {hasScore ? (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                scoreValue >= 60
                                  ? "bg-green-50 text-green-700 border border-green-200"
                                  : "bg-red-50 text-red-700 border border-red-200"
                              }`}
                            >
                              {scoreValue >= 60 ? "Passed" : "Failed"}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Pending
                            </span>
                          )}
                        </td>

                        {/* Completed Date */}
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{formatDate(result.completedAt)}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateJobSeekerModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={onRefresh}
      />

      <EditUserModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={onRefresh}
        user={selectedUser}
        userType="jobseeker"
      />

      <DeleteUserModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={onRefresh}
        user={selectedUser}
        userType="jobseeker"
      />

      <PasswordResetModal
        isOpen={passwordResetModalOpen}
        onClose={() => {
          setPasswordResetModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />
    </div>
  );
};

export default JobseekersTab;
